import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CaretDown, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { isValidGstin, lookupGstin, normalizeGstin } from "@/lib/gstinLookup";
import { GroupPicker } from "@/components/GroupPicker";
import { UnitPicker } from "@/components/UnitPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Generic searchable dropdown with a pinned "+ Add New" row. */
function SearchCombobox({ items, value, getLabel, getSub, onPick, placeholder, addLabel, onAdd, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = items.find((it) => it.id === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((it) => getLabel(it).toLowerCase().includes(q)) : items;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => { setOpen((v) => !v); setQuery(""); }}
        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm text-left flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={selected ? "text-slate-900 truncate" : "text-slate-400"}>
          {selected ? getLabel(selected) : placeholder}
        </span>
        <CaretDown size={14} className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg" data-testid={`${testId}-dropdown`}>
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search..."
              data-testid={`${testId}-search`}
              className="w-full text-sm focus:outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-400" data-testid={`${testId}-no-match`}>No match found</div>
            )}
            {filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                data-testid={`${testId}-option-${it.id}`}
                onClick={() => { onPick(it); setOpen(false); }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${it.id === value ? "bg-blue-50 font-semibold" : ""}`}
              >
                <div className="text-slate-900 truncate">{getLabel(it)}</div>
                {getSub && getSub(it) && <div className="text-[11px] text-slate-500 truncate">{getSub(it)}</div>}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid={`${testId}-add-new`}
            onClick={() => { setOpen(false); onAdd(query.trim()); }}
            className="w-full px-3 py-2.5 text-left text-sm font-bold text-blue-600 border-t border-slate-100 hover:bg-blue-50 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> {addLabel}{query.trim() ? ` "${query.trim()}"` : ""}
          </button>
        </div>
      )}
    </div>
  );
}

/** Customer picker: search-as-you-type + inline "Add New Customer" dialog. */
export function CustomerCombobox({ customers, value, onSelect, onCreated, testId = "select-customer", partyType = "sales" }) {
  const addTxt = partyType === "purchase" ? "Add Client" : "Add New Customer";
  const list = partyType === "sales" ? customers.filter((c) => (c.party_type || "sales") === "sales") : customers;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const empty = { name: "", phone: "", gstin: "", address: "", city: "", state: "Maharashtra", state_code: "27", pincode: "", group_id: "" };
  const [form, setForm] = useState(empty);
  const [gstinMsg, setGstinMsg] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!dialogOpen || groups.length) return;
    axios.get(`${API}/account-groups`).then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, [dialogOpen, groups.length]);

  const onGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setForm((f) => ({ ...f, gstin: g }));
    if (g.length < 15) { setGstinMsg(null); return; }
    if (!isValidGstin(g)) { setGstinMsg({ tone: "text-rose-600", text: "Invalid GSTIN format" }); return; }
    setGstinMsg({ tone: "text-blue-600", text: "Fetching from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        setForm((f) => ({
          ...f,
          name: d.name || f.name,
          address: d.address || f.address,
          city: d.city || f.city,
          state: d.state || f.state,
          state_code: d.state_code || f.state_code,
          pincode: d.pincode || f.pincode,
          phone: d.phone || f.phone,
        }));
        setGstinMsg({ tone: "text-emerald-700", text: `✓ ${d.name} — auto-filled` });
      } else {
        setForm((f) => ({ ...f, state: d.state || f.state, state_code: d.state_code || f.state_code }));
        setGstinMsg({ tone: "text-amber-700", text: d.message || "Only state auto-filled" });
      }
    } catch {
      setGstinMsg({ tone: "text-rose-600", text: "Lookup failed" });
    }
  };

  const save = async () => {
    if (!form.name.trim()) { setErr("Name is required"); return; }
    if (!form.group_id) { setErr("Select a ledger group in the 'Under' field"); return; }
    setSaving(true); setErr("");
    try {
      const r = await axios.post(`${API}/customers`, { ...form, name: form.name.trim(), party_type: partyType });
      onCreated(r.data);
      onSelect(r.data);
      setDialogOpen(false);
      setForm(empty);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SearchCombobox
        items={list}
        value={value}
        getLabel={(c) => c.name}
        getSub={(c) => [c.state, c.gstin].filter(Boolean).join(" · ")}
        onPick={onSelect}
        placeholder={partyType === "purchase" ? "Select Supplier / Client" : "Select Customer"}
        addLabel={addTxt}
        onAdd={(prefill) => { setForm({ ...empty, name: prefill }); setErr(""); setDialogOpen(true); }}
        testId={testId}
      />
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="quick-add-customer-dialog"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target || e.target;
            if (t?.closest?.('[data-group-picker-panel]')) e.preventDefault();
          }}
        >
          <DialogHeader><DialogTitle>{addTxt}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="qa-cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Under (Ledger Group) *</Label>
              <GroupPicker
                groups={groups}
                value={form.group_id}
                onChange={(g) => setForm((f) => ({ ...f, group_id: g.id }))}
                onGroupCreated={(g) => setGroups((gs) => [...gs, g].sort((a, b) => a.name.localeCompare(b.name)))}
                testid="qa-cust-group-picker"
              />
            </div>
            <div><Label>Phone</Label><Input data-testid="qa-cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input data-testid="qa-cust-gstin" value={form.gstin} maxLength={15} placeholder="15-digit GSTIN auto-fetches" onChange={(e) => onGstinChange(e.target.value)} />
              {gstinMsg && <p data-testid="qa-cust-gstin-status" className={`mt-1 text-[11px] font-semibold ${gstinMsg.tone}`}>{gstinMsg.text}</p>}
            </div>
            <div className="col-span-2"><Label>Address</Label><Input data-testid="qa-cust-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>City</Label><Input data-testid="qa-cust-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Pincode</Label><Input data-testid="qa-cust-pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            <div><Label>State</Label><Input data-testid="qa-cust-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>State Code</Label><Input data-testid="qa-cust-state-code" value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} /></div>
          </div>
          {err && <p className="text-sm text-rose-600" data-testid="qa-cust-error">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="button" data-testid="qa-cust-save" disabled={saving} onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white">{saving ? "Saving..." : "Save Customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Product picker: search-as-you-type + inline "Add New Item" dialog. */
export function ProductCombobox({ products, value, onSelect, onCreated, testId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const empty = { name: "", hsn_sac: "", price: "", gst_rate: "18", unit: "PCS", opening_stock: "" };
  const [form, setForm] = useState(empty);

  const save = async () => {
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true); setErr("");
    try {
      const r = await axios.post(`${API}/products`, {
        name: form.name.trim(),
        hsn_sac: form.hsn_sac.trim(),
        price: parseFloat(form.price) || 0,
        gst_rate: parseFloat(form.gst_rate) || 0,
        unit: form.unit.trim().toUpperCase() || "PCS",
        opening_stock: parseFloat(form.opening_stock) || 0,
      });
      onCreated(r.data);
      onSelect(r.data);
      setDialogOpen(false);
      setForm(empty);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const stockLabel = (p) => {
    const stock = p.current_stock ?? 0;
    const inMfg = p.in_manufacturing ?? 0;
    const unit = p.unit || "PCS";
    if (p.mfg_tracked) {
      // Manufacturing item — only finished output is sellable, raw stock isn't.
      const ready = p.ready_for_sale ?? 0;
      const raw = Math.max(Math.round((stock - ready) * 1000) / 1000, 0);
      const parts = [`Ready for sale: ${ready} ${unit}`];
      if (inMfg > 0) parts.push(`${inMfg} in mfg`);
      if (raw > 0) parts.push(`${raw} raw stock`);
      return parts.join(" · ");
    }
    if (stock <= 0 && inMfg <= 0) return "Out of stock";
    if (inMfg > 0) return `In stock: ${stock} ${unit} · ${inMfg} in mfg`;
    return `In stock: ${stock} ${unit}`;
  };

  return (
    <>
      <SearchCombobox
        items={products}
        value={value}
        getLabel={(p) => p.name}
        getSub={(p) => `GST ${p.gst_rate ?? 0}% · ${stockLabel(p)}${p.hsn_sac ? ` · HSN ${p.hsn_sac}` : ""}`}
        onPick={onSelect}
        placeholder="Select Product"
        addLabel="Add New Item"
        onAdd={(prefill) => { setForm({ ...empty, name: prefill }); setErr(""); setDialogOpen(true); }}
        testId={testId}
      />
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="quick-add-product-dialog"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target || e.target;
            if (t?.closest?.('[data-group-picker-panel]')) e.preventDefault();
          }}
        >
          <DialogHeader><DialogTitle>Add New Item</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Product Name *</Label><Input data-testid="qa-prod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>HSN/SAC</Label><Input data-testid="qa-prod-hsn" value={form.hsn_sac} onChange={(e) => setForm({ ...form, hsn_sac: e.target.value })} /></div>
            <div><Label>Price (Rs.)</Label><Input type="number" step="0.01" data-testid="qa-prod-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label>GST %</Label><Input type="number" step="0.01" data-testid="qa-prod-gst" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} /></div>
            <div>
              <Label>Unit</Label>
              <UnitPicker
                value={form.unit}
                onChange={(abbr) => setForm((f) => ({ ...f, unit: abbr }))}
                testid="qa-prod-unit-picker"
              />
            </div>
            <div><Label>Opening Stock</Label><Input type="number" step="0.01" data-testid="qa-prod-stock" value={form.opening_stock} onChange={(e) => setForm({ ...form, opening_stock: e.target.value })} /></div>
          </div>
          {err && <p className="text-sm text-rose-600" data-testid="qa-prod-error">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="button" data-testid="qa-prod-save" disabled={saving} onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white">{saving ? "Saving..." : "Save Item"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

